import { ConfirmDialog } from 'components/confirm-dialog'
import { useAppDispatch } from 'store/store'
import {
  cancelCacheTask,
  deleteUpdateTask,
  flushAllCacheTask,
  rerunTask,
  restartServersTask,
  runTask,
} from 'store/updateTasks/updateTaskSlice'
import { useTasks } from '../context/tasks-context'
import { TasksDetailsDialog } from './tasks-details-dialog'
import { TasksImportDialog } from './tasks-import-dialog'
import { TasksMutateDrawer } from './tasks-mutate-drawer'

export function TasksDialogs() {
  const { open, setOpen, currentRow, setCurrentRow } = useTasks()
  const dispatch = useAppDispatch()
  return (
    <>
      <TasksMutateDrawer
        key='task-create'
        open={open === 'create'}
        onOpenChange={() => setOpen('create')}
      />

      <TasksImportDialog
        key='tasks-import'
        open={open === 'import'}
        onOpenChange={() => setOpen('import')}
      />

      <ConfirmDialog
        key='restart-server'
        destructive
        open={open === 'restart-server'}
        onOpenChange={() => {
          setOpen('restart-server')
        }}
        handleConfirm={() => {
          setOpen(null)
          dispatch(restartServersTask())
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        }}
        className='max-w-md'
        title={'Esegui il riavvio del server?'}
        desc={
          <>
            Stai per lanciare il task di: <strong>Riavvio Server</strong>.{' '}
            <br />
            Il task verrà eseguito in background e non sarà possibile
            annullarlo. <br />
            <strong>Sei sicuro di voler continuare?</strong>
          </>
        }
        confirmText='Esegui'
      />
      <ConfirmDialog
        key='clean-cache'
        destructive
        open={open === 'clean-cache'}
        onOpenChange={() => {
          setOpen('clean-cache')
        }}
        handleConfirm={() => {
          setOpen(null)
          dispatch(cancelCacheTask())
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        }}
        className='max-w-md'
        title={'Esegui il clean della cache?'}
        desc={
          <>
            Stai per svuotare la <strong>cache dell&apos;applicazione</strong>
            : pagine dei giocatori, ricerche e contatori, che si ricalcolano dal
            database alle visite successive. <br />
            <strong>Sei sicuro di voler continuare?</strong>
          </>
        }
        confirmText='Esegui'
      />
      <ConfirmDialog
        key='flush-cache'
        destructive
        open={open === 'flush-cache'}
        onOpenChange={() => {
          setOpen('flush-cache')
        }}
        handleConfirm={() => {
          setOpen(null)
          dispatch(flushAllCacheTask())
        }}
        className='max-w-md'
        title={'Svuotare tutta la cache?'}
        desc={
          <>
            Da usare solo in emergenza: svuota <strong>tutto</strong> quello che
            il backend tiene in Redis, compresi i contatori del rate limiting e
            i dati di monitoraggio. Le pagine saranno più lente finché la cache
            non si riempie di nuovo. <br />
            <strong>Sei sicuro di voler continuare?</strong>
          </>
        }
        confirmText='Svuota tutto'
      />
      {currentRow && (
        <>
          <TasksMutateDrawer
            key={`task-update-${currentRow.id}`}
            open={open === 'update'}
            onOpenChange={() => {
              setOpen('update')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
          />

          <ConfirmDialog
            key='task-run'
            destructive
            open={open === 'run'}
            onOpenChange={() => {
              setOpen('run')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            handleConfirm={() => {
              setOpen(null)
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
              dispatch(
                runTask({
                  id: currentRow.id,
                  avoidSendMail: false,
                })
              )
            }}
            className='max-w-md'
            title={`Esegui il task: ${currentRow.file ?? '-'} ?`}
            desc={
              <>
                Stai per lanciare il task con ID:{' '}
                <strong>{currentRow.id}</strong>. <br />
                Che coinvolge il file: <strong>{currentRow.file ?? '-'}</strong>
                . <br />
                Il task verrà eseguito in background e non sarà possibile
                annullarlo. <br />
                <strong>Sei sicuro di voler continuare?</strong>
              </>
            }
            confirmText='Esegui'
          />
          <ConfirmDialog
            key='task-run-no-mail'
            destructive
            open={open === 'run-no-mail'}
            onOpenChange={() => {
              setOpen('run-no-mail')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            handleConfirm={() => {
              setOpen(null)
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
              dispatch(
                runTask({
                  id: currentRow.id,
                  avoidSendMail: true,
                })
              )
            }}
            className='max-w-md'
            title={`Esegui il task: ${currentRow.file ?? '-'} ?`}
            desc={
              <>
                Stai per lanciare il task con ID:{' '}
                <strong>{currentRow.id}</strong>. <br />
                Che coinvolge il file: <strong>
                  {currentRow.file ?? '-'}
                </strong>{' '}
                e non manderà email. <br />
                Il task verrà eseguito in background e non sarà possibile
                annullarlo. <br />
                <strong>Sei sicuro di voler continuare?</strong>
              </>
            }
            confirmText='Esegui'
          />
          {(['rerun', 'rerun-no-mail'] as const).map((dialog) => {
            const avoidSendMail = dialog === 'rerun-no-mail'
            return (
              <ConfirmDialog
                key={`task-${dialog}`}
                destructive
                open={open === dialog}
                onOpenChange={() => {
                  setOpen(dialog)
                  setTimeout(() => {
                    setCurrentRow(null)
                  }, 500)
                }}
                handleConfirm={() => {
                  setOpen(null)
                  setTimeout(() => {
                    setCurrentRow(null)
                  }, 500)
                  dispatch(rerunTask({ task: currentRow, avoidSendMail }))
                }}
                className='max-w-md'
                title={`Rilancia il task ${currentRow.id}?`}
                desc={
                  currentRow.job_class ? (
                    <>
                      Verrà rilanciato <strong>{currentRow.job_class}</strong>{' '}
                      con gli stessi argomenti
                      {currentRow.parameter_files.length > 0 &&
                        ' e gli stessi file parametri (se il job li ha già cancellati fallirà)'}
                      . Il job creerà un nuovo task. <br />
                      Se il job invia notifiche, email o dati a servizi esterni,
                      li invierà di nuovo. <br />
                      <strong>Sei sicuro di voler continuare?</strong>
                    </>
                  ) : (
                    <>
                      Il file <strong>{currentRow.file ?? '-'}</strong> verrà
                      rielaborato su questo stesso task
                      {avoidSendMail
                        ? ', senza inviare email e notifiche'
                        : ', inviando email e notifiche ai giocatori'}
                      . <br />
                      Il task verrà eseguito in background e non sarà possibile
                      annullarlo. <br />
                      <strong>Sei sicuro di voler continuare?</strong>
                    </>
                  )
                }
                confirmText='Rilancia'
              />
            )
          })}
          <TasksDetailsDialog
            key={`task-details-${currentRow.id}`}
            task={currentRow}
            open={open === 'details'}
            onOpenChange={() => {
              setOpen('details')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
          />
          <ConfirmDialog
            key='task-delete'
            destructive
            open={open === 'delete'}
            onOpenChange={() => {
              setOpen('delete')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            handleConfirm={() => {
              setOpen(null)
              setTimeout(() => {
                setCurrentRow(null)
                window.location.reload()
              }, 2000)
              dispatch(deleteUpdateTask(currentRow.id))
            }}
            className='max-w-md'
            title={`Cancella il task: ${currentRow.file ?? '-'} ?`}
            desc={
              <>
                Stai per eliminare il task con ID:{' '}
                <strong>{currentRow.id}</strong>, <br />
                che coinvolge il file: <strong>{currentRow.file ?? '-'}</strong>
                . <br />
                <strong>
                  Questa azione non può essere annullata. Sei sicuro di voler
                  continuare?
                </strong>
              </>
            }
            confirmText='Cancella'
          />
        </>
      )}
    </>
  )
}
